import React from 'react';
import ParameterTable from './ParameterTable';
import EndpointCard from './EndpointCard';
import CodeBlock from './CodeBlock';
import ParameterTableDesc from "./ParameterTableDesc";
import { CheckCircle, LooksOne, LooksTwo, Looks3, Looks4, Looks5, Link as LinkIcon, Security, ArrowBack, ArrowForward } from '@mui/icons-material';

const ApiSection = ({ section, sections = [], activeSection, onSectionChange }) => {
    if (!section) return null;

    const currentIndex = sections.findIndex(s => s.id === activeSection);
    const prevSection = currentIndex > 0 ? sections[currentIndex - 1] : null;
    const nextSection = currentIndex < sections.length - 1 ? sections[currentIndex + 1] : null;

    return (
        <div className="p-6 sm:p-8">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold dark:text-slate-400">
                    Section
                </div>
                <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                    {section.title}
                </h1>
            </div>
            <GenericSection section={section} />
            
            {/* Navigation Précédent/Suivant */}
            {(prevSection || nextSection) && (
                <div className="mt-12 pt-8 border-t border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between">
                    {prevSection ? (
                        <button
                            onClick={() => onSectionChange?.(prevSection.id)}
                            className="group flex items-center space-x-2 px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-2xl transition-all border border-transparent hover:border-slate-200/80 dark:text-slate-200 dark:hover:bg-slate-800/50 dark:hover:border-slate-700/70"
                        >
                            <ArrowBack fontSize="small" />
                            <div className="text-left">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Précédent</div>
                                <div className="font-semibold">{prevSection.title}</div>
                            </div>
                        </button>
                    ) : (
                        <div></div>
                    )}
                    {nextSection ? (
                        <button
                            onClick={() => onSectionChange?.(nextSection.id)}
                            className="group flex items-center space-x-2 px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-2xl transition-all border border-transparent hover:border-slate-200/80 dark:text-slate-200 dark:hover:bg-slate-800/50 dark:hover:border-slate-700/70"
                        >
                            <div className="text-right">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Suivant</div>
                                <div className="font-semibold">{nextSection.title}</div>
                            </div>
                            <ArrowForward fontSize="small" />
                        </button>
                    ) : (
                        <div></div>
                    )}
                </div>
            )}
        </div>
    );
};

const GenericSection = ({ section }) => {
    const { content } = section;

    return (
        <div className="space-y-8">
            {section.id === 'introduction' ? (
                <GettingStarted content={content} />
            ) : content?.description ? (
        <p className="text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-line">{content.description}</p>
            ) : null}

            {content?.access?.warning && (
                <div className="bg-blue-50/80 border border-blue-200/80 rounded-2xl p-5 flex gap-4 dark:bg-blue-500/10 dark:border-blue-400/20">
                    <div className="w-11 h-11 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0 dark:bg-blue-500/15">
                        <Security className="text-blue-700 dark:text-blue-300" />
                    </div>
                    <div>
                        {content.access.title ? (
                            <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">{content.access.title}</h3>
                        ) : null}
                        {content.access.description ? <p className="text-blue-800 dark:text-blue-200/90">{content.access.description}</p> : null}
                        <p className="text-blue-900 dark:text-blue-200 font-semibold">
                            {stripBoldMarkdown(content.access.warning)}
                        </p>
        </div>
    </div>
            )}

            {content?.baseUrls?.production && section.id !== 'identifiants-comptes' && (
                <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 dark:bg-slate-900/30 dark:border-slate-700/60">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">URLs de base</h3>
                    <div className="font-mono text-sm text-slate-800 dark:text-slate-200 bg-white border border-slate-200 rounded-xl px-4 py-3 dark:bg-slate-950/30 dark:border-slate-700/70">
                        Production: {content.baseUrls.production}
                    </div>
                </div>
            )}

            {section.id === 'payment-request' && content?.paymentRequest ? (
                <PaymentRequestFlows content={content.paymentRequest} />
            ) : Array.isArray(content?.endpoints) && content.endpoints.length > 0 && section.id !== 'identifiants-comptes' ? (
                <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">Endpoints</h3>
                    <div className="space-y-4">
                        {content.endpoints.map((ep, idx) => (
                            <EndpointCard key={`${ep.method}-${ep.path}-${idx}`} method={ep.method} path={ep.path} description={ep.description} />
                        ))}
                    </div>
                </div>
            ) : null}

            {section.id === 'identifiants-comptes' && (
                <div className="space-y-8">
                    {content?.baseUrls?.production && (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">URL de base (service Comptes)</h3>
                            </div>
                            <div className="p-5">
                                <code className="text-sm font-mono text-gray-800 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 block">{content.baseUrls.production}</code>
                            </div>
                        </div>
                    )}
                    {Array.isArray(content?.endpoints) && content.endpoints.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Endpoints disponibles</h3>
                            </div>
                            <ul className="divide-y divide-gray-100">
                                {content.endpoints.map((ep, idx) => (
                                    <li key={idx} className="px-5 py-4 hover:bg-gray-50/50 transition-colors">
                                        <div className="flex flex-wrap items-start gap-2">
                                            <span className={`inline-flex px-2.5 py-0.5 rounded text-xs font-semibold ${ep.method === 'GET' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                                                {ep.method}
                                            </span>
                                            <code className="text-sm font-mono text-gray-800 break-all">{ep.path}</code>
                                        </div>
                                        {ep.description && <p className="mt-2 text-sm text-gray-600 leading-relaxed">{ep.description}</p>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {content?.identifiantsClarification && Object.keys(content.identifiantsClarification).length > 0 && (
                        <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-5">
                            <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide mb-3">Points importants à retenir</h3>
                            <ul className="space-y-2">
                                {Object.entries(content.identifiantsClarification).map(([point, detail]) => (
                                    <li key={point} className="text-sm text-amber-900">
                                        <strong>{point} :</strong> {detail}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {Array.isArray(content?.identifiantsMapping) && content.identifiantsMapping.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                            <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
                                <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Correspondance des champs</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Champ</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Source</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Champ en réponse</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {content.identifiantsMapping.map((m, index) => (
                                            <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-5 py-3"><code className="text-sm font-mono text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-100">{m.champ}</code></td>
                                                <td className="px-5 py-3 text-sm text-gray-700 leading-relaxed">{m.source}</td>
                                                <td className="px-5 py-3 text-sm text-gray-600 font-mono">{m.champReponse}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {section.id === 'payment-verification' && content?.webhookConfig && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-8">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">{content.webhookConfig.title}</h3>
                    </div>
                    <div className="p-5 space-y-5">
                        {content.webhookConfig.basePath && (
                            <p className="text-sm text-gray-600 leading-relaxed">{content.webhookConfig.basePath}</p>
                        )}
                        {Array.isArray(content.webhookConfig.endpoints) && content.webhookConfig.endpoints.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Endpoints API</h4>
                                <ul className="space-y-3">
                                    {content.webhookConfig.endpoints.map((ep, idx) => (
                                        <li key={idx} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className={`inline-flex px-2.5 py-0.5 rounded text-xs font-semibold ${
                                                    ep.method === 'GET' ? 'bg-emerald-100 text-emerald-800' :
                                                    ep.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                                                    ep.method === 'PUT' ? 'bg-amber-100 text-amber-800' :
                                                    ep.method === 'DELETE' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'
                                                }`}>{ep.method}</span>
                                                <code className="text-sm font-mono text-gray-800">{ep.path}</code>
                                                {ep.params && <span className="text-xs text-gray-500">({ep.params})</span>}
                                            </div>
                                            {ep.description && <p className="mt-2 text-sm text-gray-600">{ep.description}</p>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {Array.isArray(content.webhookConfig.requestBody) && content.webhookConfig.requestBody.length > 0 && (
                            <ParameterTable title="Corps de la requête (POST / PUT)" parameters={content.webhookConfig.requestBody} />
                        )}
                        {content.webhookConfig.responseNote && (
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-900">
                                {content.webhookConfig.responseNote}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {Array.isArray(content?.webhookHeaders) && content.webhookHeaders.length > 0 && section.id === 'payment-verification' && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Entêtes envoyés par Netikash</h3>
                    </div>
                    <div className="p-5">
                        <ParameterTable title="" parameters={content.webhookHeaders} />
                    </div>
                </div>
            )}
            {Array.isArray(content?.headers) && content.headers.length > 0 && (
                <ParameterTable title="Entêtes (Headers)" parameters={content.headers} />
            )}

            {Array.isArray(content?.requestBody) && content.requestBody.length > 0 && (
                <ParameterTable title="Contenu de la requête (Request body)" parameters={content.requestBody} />
            )}

            {Array.isArray(content?.responseBody) && content.responseBody.length > 0 && (
                <ParameterTable title="Réponse (Response body)" parameters={content.responseBody} />
            )}

            {/* Rate Limits */}
            {section.id === 'rate-limits' && (content?.rateLimits || content?.rateLimitsNote) && (
                <div className="space-y-6">
                    {Array.isArray(content.rateLimits) && content.rateLimits.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Limites par endpoint</h3>
                            <div className="space-y-3">
                                {content.rateLimits.map((limit, idx) => (
                                    <div key={idx} className="bg-white rounded-lg p-4 border border-amber-200">
                                        <code className="text-sm font-mono text-gray-900">{limit.endpoint}</code>
                                        <div className="mt-2 text-sm text-gray-700">
                                            <span className="font-semibold">{limit.limit}</span> par <span className="font-semibold">{limit.window}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {content.headers && (
                        <ParameterTable title="Headers de réponse (si renvoyés)" parameters={content.headers.map(h => ({
                            name: h.name,
                            type: 'string',
                            required: 'non',
                            description: h.description
                        }))} />
                    )}
                </div>
            )}

            {/* Idempotency */}
            {section.id === 'idempotency' && (
                <div className="space-y-6">
                    {content?.idempotencyNotSupported && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                            <p className="text-amber-900 font-medium">Fonctionnalité à venir. En attendant, utilisez une référence unique par demande côté client pour éviter les doublons.</p>
                        </div>
                    )}
                    {content?.keyHeader && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Header Idempotency-Key</h3>
                            <div className="space-y-2">
                                <div>
                                    <code className="text-sm font-mono bg-white px-2 py-1 rounded border">{content.keyHeader.name}</code>
                                </div>
                                <p className="text-gray-700">{content.keyHeader.description}</p>
                                <p className="text-sm text-gray-600">Format: {content.keyHeader.format}</p>
                            </div>
                        </div>
                    )}
                    {content?.behavior && (
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Comportement</h3>
                            <ul className="space-y-2">
                                {content.behavior.map((item, idx) => (
                                    <li key={idx} className="flex items-start space-x-2 text-gray-700">
                                        <CheckCircle className="text-green-500 mt-1" fontSize="small" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {content?.example?.curl && (
                        <CodeBlock code={content.example.curl} language="curl" />
                    )}
                </div>
            )}

            {/* Webhook Retries */}
            {section.id === 'webhook-retries' && (
                <div className="space-y-6">
                    {content?.strategy && (
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Stratégie de retry</h3>
                            <div className="space-y-3">
                                {content.strategy.map((item, idx) => (
                                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold text-gray-900">Tentative {item.attempt}</span>
                                            <span className="text-sm text-gray-600">Délai: {item.delay}</span>
                                        </div>
                                        <p className="text-sm text-gray-700">{item.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {content?.timeout && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                            <p className="text-sm text-yellow-900"><strong>Timeout:</strong> {content.timeout}</p>
                        </div>
                    )}
                    {content?.bestPractices && (
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Bonnes pratiques</h3>
                            <ul className="space-y-2">
                                {content.bestPractices.map((item, idx) => (
                                    <li key={idx} className="flex items-start space-x-2 text-gray-700">
                                        <CheckCircle className="text-blue-500 mt-1" fontSize="small" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Error codes métier */}
            {section.id === 'error-codes-metier' && (
                <div className="space-y-6">
                    {content?.errorResponseFormat && (
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">{content.errorResponseFormat.title}</h3>
                            {Array.isArray(content.errorResponseFormat.fields) && content.errorResponseFormat.fields.length > 0 && (
                                <ParameterTable
                                    title="Champs de la réponse"
                                    parameters={content.errorResponseFormat.fields.map(f => ({ name: f.name, type: f.type, required: '-', description: f.description }))}
                                />
                            )}
                            {content.errorResponseFormat.example && (
                                <CodeBlock code={content.errorResponseFormat.example} language="json" />
                            )}
                        </div>
                    )}
                    {Array.isArray(content?.errorCodes) && content.errorCodes.length > 0 && (
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Référence des codes (target)</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code (target)</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {content.errorCodes.map((err, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-3"><code className="text-sm font-mono text-amber-600 bg-amber-50 px-2 py-1 rounded">{err.target}</code></td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{err.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* HTTP Codes */}
            {section.id === 'http-codes' && content?.codes && (
                <div className="space-y-4">
                    {content.codes.map((httpCode, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-xl p-5">
                            <div className="flex items-center space-x-3 mb-2">
                                <code className="px-3 py-1 bg-gray-100 border border-gray-300 rounded font-mono font-semibold text-gray-900">
                                    {httpCode.code}
                                </code>
                                <h3 className="text-lg font-semibold text-gray-900">{httpCode.name}</h3>
                            </div>
                            <p className="text-gray-700 mb-3">{httpCode.description}</p>
                            {httpCode.examples && (
                                <div>
                                    <span className="text-sm font-medium text-gray-600">Exemples:</span>
                                    <ul className="mt-1 space-y-1">
                                        {httpCode.examples.map((example, exIdx) => (
                                            <li key={exIdx} className="text-sm text-gray-600 flex items-start space-x-2">
                                                <span className="text-gray-400">•</span>
                                                <span>{example}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {content?.examples?.curl && (
                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Exemple (cURL)</h3>
                    <CodeBlock code={content.examples.curl} language="bash" />
                </div>
            )}

            {Array.isArray(content?.eventTypes) && content.eventTypes.length > 0 && section.id === 'payment-verification' && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Types d'événements</h3>
                    </div>
                    <div className="p-5">
                        <ParameterTableDesc title="" parameters={content.eventTypes} />
                    </div>
                </div>
            )}

            {content?.examples?.webhookPayload && section.id === 'payment-verification' && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Exemple de payload webhook</h3>
                    </div>
                    <div className="p-5">
                        <CodeBlock code={content.examples.webhookPayload} language="json" />
                    </div>
                </div>
            )}

            {Array.isArray(content?.statuses) && content.statuses.length > 0 && section.id === 'payment-verification' && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                    <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100">
                        <h3 className="text-sm font-semibold text-emerald-900 uppercase tracking-wide">Statuts de paiement</h3>
                    </div>
                    <div className="p-5">
                        <ul className="space-y-2">
                            {content.statuses.map((s) => (
                                <li key={String(s)} className="flex items-center gap-3 text-sm text-gray-700">
                                    <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"/>
                                    <span className="font-mono">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Webhook signature verification */}
            {section.id === 'payment-verification' && content?.webhookSignature && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Vérification de la signature</h3>
                    </div>
                    <div className="p-5 space-y-4">
                        <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                            <p className="text-gray-700 text-sm mb-2"><strong>Algorithme :</strong> {content.webhookSignature.algorithm}</p>
                            <p className="text-gray-700 text-sm"><strong>Headers envoyés :</strong> {content.webhookSignature.headersSent}</p>
                        </div>
                        {content.webhookSignature.verifyExample && (
                            <CodeBlock code={content.webhookSignature.verifyExample} language="javascript" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

function PaymentRequestFlows({ content }) {
    const { fluxA, verificationById } = content;
    return (
        <div className="space-y-10">
            {fluxA && (
                <div className="border border-gray-200 rounded-xl p-6 bg-blue-50/30">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">{fluxA.title}</h3>
                    {fluxA.subtitle && <p className="text-gray-600 text-sm mb-4">{fluxA.subtitle}</p>}
                    <EndpointCard method={fluxA.method} path={fluxA.path} description="" />
                    {fluxA.requestBody?.length > 0 && <ParameterTable title="Paramètres de la requête" parameters={fluxA.requestBody} />}
                    {fluxA.responseBody?.length > 0 && <ParameterTable title="Réponse" parameters={fluxA.responseBody} />}
                    {fluxA.curl && <CodeBlock code={fluxA.curl} language="bash" />}
                </div>
            )}
            {verificationById && (
                <div className="border border-gray-200 rounded-xl p-6 bg-green-50/30">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{verificationById.title}</h3>
                    <p className="text-gray-700 mb-4">{verificationById.description}</p>
                    <EndpointCard method={verificationById.method} path={verificationById.path} description="" />
                    {verificationById.curl && <CodeBlock code={verificationById.curl} language="bash" />}
                </div>
            )}
        </div>
    );
}

const NUMBER_ICONS = {
    1: LooksOne,
    2: LooksTwo,
    3: Looks3,
    4: Looks4,
    5: Looks5
};

function stripBoldMarkdown(text) {
    if (!text) return '';
    return String(text).replace(/\*\*/g, '');
}

function linkifyText(text) {
    const raw = String(text || '');
    const parts = raw.split(/(https?:\/\/\S+)/g);
    return parts.map((part, idx) => {
        if (!part) return null;
        if (/^https?:\/\/\S+$/g.test(part)) {
            // remove trailing punctuation that often sticks to URLs
            const match = part?.match(/^(https?:\/\/[^\s)\]}>,]+)([)\]}>,.]*)$/);

            const url = match ? match[1] : part;
            const trailing = match ? match[2] : '';
            return (
                <React.Fragment key={`${url}-${idx}`}>
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 underline underline-offset-4"
                    >
                        <LinkIcon fontSize="inherit" />
                        <span className="font-medium">{url}</span>
                    </a>
                    {trailing}
                </React.Fragment>
            );
        }
        return <React.Fragment key={`${idx}-${part.slice(0, 8)}`}>{part}</React.Fragment>;
    });
}

function parseSteps(description) {
    const lines = String(description || '')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);

    const steps = [];
    let current = null;

    for (const line of lines) {
        const top = line.match(/^(\d+)\.\s*(.+)$/);
        const sub = line.match(/^([a-zA-Z])\.\s*(.+)$/);

        if (top) {
            if (current) steps.push(current);
            current = { n: Number(top[1]), title: top[2], bullets: [] };
            continue;
        }

        if (sub && current) {
            current.bullets.push(sub[2]);
            continue;
        }

        if (!current) {
            current = { n: 1, title: line, bullets: [] };
            continue;
        }

        // attach to last bullet if exists, otherwise to title
        if (current.bullets.length > 0) current.bullets[current.bullets.length - 1] += ` ${line}`;
        else current.title += ` ${line}`;
    }

    if (current) steps.push(current);
    return steps;
}

const GettingStarted = ({ content }) => {
    const steps = parseSteps(content?.description);

    return (
    <div className="space-y-6">
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-gray-900">Démarrage</h2>
                <p className="text-gray-700 mt-2">
                    Suivez ces étapes pour obtenir vos identifiants et effectuer vos premiers appels API.
                </p>
            </div>

            <div className="grid gap-6">
                {steps.map((s) => {
                    const Icon = NUMBER_ICONS[s.n] || LooksOne;
                    return (
                        <div key={s.n} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <Icon className="text-amber-700" />
                    </div>
                                <div className="min-w-0">
                                    <h3 className="text-lg font-bold text-gray-900">{linkifyText(s.title)}</h3>
                                    {s.bullets.length > 0 && (
                                        <ul className="mt-4 space-y-3">
                                            {s.bullets.map((b, idx) => (
                                                <li key={`${s.n}-${idx}`} className="flex items-start gap-3 text-gray-700">
                                                    <CheckCircle className="text-amber-600 mt-0.5" fontSize="small" />
                                                    <span className="leading-relaxed">{linkifyText(b)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    </div>
);
};

export default ApiSection;